import {
  FetchWrapper,
  Layout,
  NestedRoutes,
  SocketGlobalConnection,
} from 'element';
import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { BOX_AUTH_GET_GLOBAL_DATA_REQUEST } from 'state/Auth';
import { PRIVATE_LAYOUT } from 'state/FetchPageData';
import { Route, StringMap } from 'type';
import HelpMenuItemConfirmationDialog from './HelpMenuItemConfirmationDialog';
import RosterHeader from './RosterHeader';
import RosterMenu from './RosterMenu';
import ActivateTimeClockMenuItemConfirmationDialog from './ActivateTimeClockMenuItemConfirmationDialog';
import { WithFeatureFlagServiceProvider } from 'flags/WithFeatureFlagServiceProvider';
import { FeatureFlag } from 'element/feature-flags-components';
import NewNavigationLayout from './NewNavigation/NewNavigationLayout';
import { throttle } from 'lodash';

type Props = {
  routes: StringMap<Route>;
  location: String;
};

export default function PrivateLayout({ routes: nestedRoutes }: Props) {
  const dispatch = useDispatch();
  const fetchData = useCallback(() => {
    dispatch(BOX_AUTH_GET_GLOBAL_DATA_REQUEST());
  }, [dispatch]);

  // If the user has been inactive for more than an hour reload the page
  // this will log them out if their refresh token has expired
  useEffect(() => {
    let timerId: NodeJS.Timeout | null = null;

    const startTimer = () => {
      timerId = setTimeout(() => {
        window.location.href = process.env.REACT_APP_BREATHE_HR_URL as string; // redirect user to HR, HR will log them out
      }, 60 * 60 * 1000); // 1 hour
    };

    const stopTimer = () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };

    const resetTimer = () => {
      stopTimer();
      startTimer();
    };

    const throttledTimer = throttle(resetTimer, 300);

    startTimer();
    window.addEventListener('click', resetTimer);
    window.addEventListener('mousemove', throttledTimer);
    window.addEventListener('keydown', resetTimer);

    return () => {
      stopTimer();
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('mousemove', throttledTimer);
      window.removeEventListener('keydown', resetTimer);
    };
  }, []);

  const showNewNavigation: boolean = true;

  const pageContent = (
    <>
      <NestedRoutes routes={nestedRoutes} />
      <HelpMenuItemConfirmationDialog />
      <ActivateTimeClockMenuItemConfirmationDialog />
    </>
  );

  return (
    <FetchWrapper fetchData={fetchData} pageId={PRIVATE_LAYOUT}>
      <WithFeatureFlagServiceProvider>
        <SocketGlobalConnection />

        {showNewNavigation ? (
          /* New navigation layout */
          <NewNavigationLayout>{pageContent}</NewNavigationLayout>
        ) : (
          /* Old navigation layout */
          <Layout header={<RosterHeader />} menu={<RosterMenu />}>
            {pageContent}
          </Layout>
        )}
      </WithFeatureFlagServiceProvider>
    </FetchWrapper>
  );
}
